<template>
    <div class="Tabs">
        <div class="Tabs__headerContainer">
            <div class="Tabs__headers">
                <div
                    v-for="tab in tabs"
                    :key="tab.id"
                    class="Tabs__header"
                    :class="{'Tabs__header--active': tab.id === activeTab}"
                    @click="onTabChange(tab)"
                >
                    <slot :name="`tab-header-${tab.id}`" v-bind="{ tab }">
                        {{ tab.name }}
                    </slot>
                </div>
            </div>
            <div v-if="$slots.headerEnd" class="Tabs__headerEnd">
                <slot name="headerEnd" />
            </div>
        </div>

        <div class="Tabs__content">
            <slot :active-tab-id="activeTab" />
        </div>
    </div>
</template>
<script lang="ts">
    import { Component, Vue, Prop, Watch, Provide } from 'vue-property-decorator';

    import { TABS_REGISTRY, TabMeta, TabsRegistry } from './types';

    @Component({
        emits: [ 'change' ],
    })
    export default class Tabs extends Vue {
        // also act as a switch to emit tab changes instead of self control
        @Prop({ type: String, default: null })
        private selectedTab!: string | null;

        @Prop({ type: String, default: null })
        private initialTab!: string | null;

        // PRODUCT-26110 Phase 2B2: replaces the old `$children`/`$vnode` discovery.
        // <Tab> children self-register here (in DOM/mount order) via the provided
        // registry; `isActive` reads happen inside each Tab's reactive getter, so they
        // track this component's reactive `activeTab`.
        private tabs: TabMeta[] = [];
        private internalActiveTab: string | null = null;

        @Provide(TABS_REGISTRY)
        private registry: TabsRegistry = {
            activeId: undefined,
            register: (tab) => this.registerTab(tab),
            unregister: (id) => this.unregisterTab(id),
        };

        // Mirror the computed active tab into the reactive registry field the Tabs read.
        @Watch('activeTab', { immediate: true })
        private onActiveTabChange() {
            this.registry.activeId = this.activeTab;
        }

        @Watch('selectedTab')
        private onSelectedTabExternalChange() {
            this.internalActiveTab = this.selectedOrDefaultTab ?? null;
        }

        private registerTab(tab: TabMeta): void {
            if (!this.tabs.some((existing) => existing.id === tab.id)) {
                this.tabs = [ ...this.tabs, tab ];
            }
            if (this.internalActiveTab == null) {
                this.internalActiveTab = this.selectedOrDefaultTab ?? null;
            }
        }

        private unregisterTab(id: string): void {
            this.tabs = this.tabs.filter((tab) => tab.id !== id);
            if (this.internalActiveTab === id) {
                this.internalActiveTab = this.selectedOrDefaultTab ?? null;
            }
        }

        private onTabChange(tab: TabMeta): void {
            this.$emit('change', tab.id);
            if (!this.isControlledTabs) {
                this.internalActiveTab = tab.id;
            }
        }

        private get isControlledTabs(): boolean {
            return this.selectedTab !== null;
        }

        private get activeTab(): string | undefined {
            if (this.isControlledTabs) {
                return this.selectedOrDefaultTab ?? undefined;
            }
            return (this.internalActiveTab ?? this.selectedOrDefaultTab) ?? undefined;
        }

        private get selectedOrDefaultTab(): string | null | undefined {
            const availableTabs: string[] = this.tabs.map((tab) => tab.id);
            return [ this.selectedTab, this.initialTab, availableTabs[0] ]
                .find((tab) => tab != null && availableTabs.includes(tab));
        }
    }
</script>
